import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Sparkles, Send, Check, AlertCircle, CheckCircle, Calendar, Undo2 } from 'lucide-react';
import API_CONFIG from '../config/api';
import { enTeteComptePrive } from '../utils/session';

interface Journee {
  date: string;
  clientsPrevus: number;
  // Absent des analyses faites avant le passage au temps de route
  minutesDuPlusProche?: number | null;
  kmDuPlusProche: number | null;
  plusProche: string | null;
}

/** Conversation renvoyée par GET /api/sms */
interface Conversation {
  _id: string;
  nom: string;
  telephone: string;
  messages: { texte: string; recuLe: string }[];
  dernierTexteLe: string;
  statut: 'a_traiter' | 'traite';
  analyse?: {
    statut?: 'en_cours' | 'pret' | 'erreur';
    messagesLus?: number;
    demandeRdv?: boolean;
    service?: string;
    resume?: string;
    journees?: Journee[];
    rdvAVenir?: string | null;
    sansAdresse?: boolean;
    reponse?: string | null;
    erreur?: string;
  };
}

const jourCourt = (date: string) =>
  new Date(`${date}T12:00:00`).toLocaleDateString('fr-CA', { weekday: 'short', day: 'numeric', month: 'short' });

const quand = (iso: string) => {
  const d = new Date(iso);
  const heure = d.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' });
  return d.toDateString() === new Date().toDateString()
    ? heure
    : `${d.toLocaleDateString('fr-CA', { weekday: 'short', day: 'numeric', month: 'short' })} · ${heure}`;
};

// iOS ouvre Messages avec le texte déjà écrit ; Massil n'a plus qu'à appuyer sur Envoyer
const lienSms = (telephone: string, texte: string) => `sms:${telephone}&body=${encodeURIComponent(texte)}`;

const CarteConversation = ({
  c,
  onStatut,
  onAnalyser,
}: {
  c: Conversation;
  onStatut: (id: string, statut: Conversation['statut']) => void;
  onAnalyser: (id: string) => void;
}) => {
  const a = c.analyse;
  const [reponse, setReponse] = useState(a?.reponse || '');
  useEffect(() => setReponse(a?.reponse || ''), [a?.reponse]);

  const perimee = a?.statut === 'pret' && a.messagesLus !== c.messages.length;
  const sansAdresse = !!a?.journees?.length && !!a.sansAdresse;

  return (
    <div
      className={`rounded-xl border p-4 ${
        c.statut === 'traite'
          ? 'border-gray-700/50 bg-gray-900/40 opacity-70'
          : 'border-indigo-500/30 bg-gradient-to-br from-gray-900/60 to-gray-800/50'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="truncate font-semibold text-gray-100">{c.nom}</p>
        <span className="shrink-0 text-[11px] text-gray-500">{quand(c.dernierTexteLe)}</span>
      </div>

      {/* Les derniers textos, du plus ancien au plus récent */}
      <div className="mt-2 space-y-1.5">
        {c.messages.slice(-3).map((m, i) => (
          <p key={i} className="w-fit max-w-full rounded-2xl rounded-bl-sm bg-gray-700/60 px-3 py-1.5 text-sm text-gray-100">
            {m.texte}
          </p>
        ))}
      </div>

      {c.statut === 'a_traiter' && (
        <div className="mt-3">
          {a?.statut === 'en_cours' && (
            <p className="flex items-center gap-2 text-xs text-gray-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-cyan-300" /> Gemma lit les textos…
            </p>
          )}

          {a?.statut === 'erreur' && (
            <p className="flex items-start gap-1.5 text-xs text-rose-300">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {a.erreur || 'Analyse impossible'}
            </p>
          )}

          {a?.statut === 'pret' && (
            <div className="space-y-2.5 rounded-lg bg-gray-950/40 px-3 py-2.5">
              <div className="flex flex-wrap items-center gap-1.5">
                {a.service && (
                  <span
                    className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                      a.demandeRdv ? 'bg-cyan-500/20 text-cyan-200' : 'bg-gray-700/60 text-gray-300'
                    }`}
                  >
                    {a.demandeRdv ? `RDV ${a.service.toLowerCase()}` : a.service}
                  </span>
                )}
                {perimee && <span className="text-[11px] text-amber-300">Nouveau texto depuis l'analyse</span>}
                <button
                  onClick={() => onAnalyser(c._id)}
                  className="ml-auto flex items-center gap-1 text-[11px] text-gray-500 hover:text-cyan-300"
                >
                  <Sparkles className="h-3 w-3" /> Réanalyser
                </button>
              </div>
              {a.resume && <p className="text-sm text-gray-300">{a.resume}</p>}
              {a.rdvAVenir && <p className="text-xs text-amber-200">Déjà prévu : {a.rdvAVenir}</p>}

              {!!a.journees?.length && (
                <div className="space-y-1">
                  {a.journees.map((j) => (
                    <Link
                      key={j.date}
                      to={`/schedule?date=${j.date}`}
                      className="flex items-center justify-between gap-2 rounded-md border border-cyan-400/20 bg-cyan-500/5 px-2.5 py-1.5 text-xs transition-all hover:bg-cyan-500/15"
                    >
                      <span className="flex items-center gap-1.5 whitespace-nowrap text-cyan-100">
                        <Calendar className="h-3.5 w-3.5" /> {jourCourt(j.date)}
                      </span>
                      <span className="min-w-0 truncate text-right text-gray-400">
                        {j.clientsPrevus === 0
                          ? 'Journée libre'
                          : `${j.clientsPrevus} prévu${j.clientsPrevus > 1 ? 's' : ''}`}
                        {j.minutesDuPlusProche != null ? (
                          <>
                            {' · '}
                            {/* Au-delà de 20 min, le voisin ne vaut plus vraiment le détour */}
                            <span className={j.minutesDuPlusProche > 20 ? 'text-amber-300' : 'font-medium text-emerald-300'}>
                              {j.minutesDuPlusProche} min
                            </span>{' '}
                            de {j.plusProche}
                          </>
                        ) : (
                          j.kmDuPlusProche !== null && ` · ${j.kmDuPlusProche.toLocaleString('fr-CA')} km de ${j.plusProche}`
                        )}
                      </span>
                    </Link>
                  ))}
                  {sansAdresse && (
                    <p className="text-[11px] text-amber-300">Pas d'adresse sur sa fiche : ce sont les premières journées libres.</p>
                  )}
                </div>
              )}
              {a.demandeRdv && !a.journees?.length && (
                <p className="text-xs text-amber-300">Aucune journée disponible dans la période demandée.</p>
              )}
            </div>
          )}

          {/* Réponse proposée, modifiable avant l'envoi */}
          {a?.statut === 'pret' && (
            <textarea
              value={reponse}
              onChange={(e) => setReponse(e.target.value)}
              rows={4}
              placeholder="Ta réponse…"
              className="mt-2.5 w-full resize-y rounded-lg border border-gray-600/50 bg-gray-950/60 px-3 py-2 text-sm text-gray-100 placeholder:text-gray-600 focus:border-cyan-400/60 focus:outline-none"
            />
          )}

          <div className="mt-2.5 flex gap-2">
            {a?.statut === 'pret' && reponse.trim() ? (
              <a
                href={lienSms(c.telephone, reponse)}
                onClick={() => onStatut(c._id, 'traite')}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-cyan-400/40 bg-cyan-500/20 px-2 py-2 text-xs font-medium text-cyan-100 transition-all hover:bg-cyan-500/30"
              >
                <Send className="h-3.5 w-3.5" /> Répondre par texto
              </a>
            ) : (
              a?.statut !== 'en_cours' && (
                <button
                  onClick={() => onAnalyser(c._id)}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-indigo-400/40 bg-indigo-500/15 px-2 py-2 text-xs text-indigo-100 transition-all hover:bg-indigo-500/25"
                >
                  <Sparkles className="h-3.5 w-3.5" /> Analyser
                </button>
              )
            )}
            <button
              onClick={() => onStatut(c._id, 'traite')}
              className="flex items-center justify-center gap-1.5 rounded-lg border border-gray-600/40 bg-gray-700/30 px-3 py-2 text-xs text-gray-300 transition-all hover:bg-gray-700/50"
            >
              <Check className="h-3.5 w-3.5" /> Traité
            </button>
          </div>
        </div>
      )}

      {c.statut === 'traite' && (
        <button
          onClick={() => onStatut(c._id, 'a_traiter')}
          className="mt-2 flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-300"
        >
          <Undo2 className="h-3 w-3" /> Remettre à traiter
        </button>
      )}
    </div>
  );
};

const Textos = () => {
  const [conversations, setConversations] = useState<Conversation[] | null>(null);
  const [analyseAuto, setAnalyseAuto] = useState(false);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');

  const charger = useCallback(async (silencieux = false) => {
    if (!silencieux) setChargement(true);
    try {
      const resp = await fetch(API_CONFIG.endpoints.textos, { headers: enTeteComptePrive() });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Impossible de charger les textos');
      setConversations(data.conversations || []);
      setAnalyseAuto(!!data.analyseAuto);
      setErreur('');
    } catch (err) {
      setErreur(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);

  // Mise à jour automatique : les nouveaux textos arrivent d'eux-mêmes, et
  // toutes les 3 s tant que Gemma travaille pour suivre la progression.
  // Rien pendant que l'app est en arrière-plan ; rechargée dès le retour.
  const enCours = !!conversations?.some((c) => c.analyse?.statut === 'en_cours');
  useEffect(() => {
    const minuterie = setInterval(() => {
      if (document.visibilityState === 'visible') charger(true);
    }, enCours ? 3000 : 15000);
    const auRetour = () => {
      if (document.visibilityState === 'visible') charger(true);
    };
    document.addEventListener('visibilitychange', auRetour);
    return () => {
      clearInterval(minuterie);
      document.removeEventListener('visibilitychange', auRetour);
    };
  }, [enCours, charger]);

  const appeler = async (url: string, methode: string, corps: unknown) => {
    try {
      const resp = await fetch(url, { method: methode, headers: { 'Content-Type': 'application/json', ...enTeteComptePrive() }, body: JSON.stringify(corps) });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Erreur');
    } catch (err) {
      setErreur(err instanceof Error ? err.message : 'Erreur inconnue');
    }
    charger(true);
  };

  const analyser = (ids?: string[]) => appeler(API_CONFIG.endpoints.textosAnalyser, 'POST', ids ? { ids } : {});

  const changerStatut = (id: string, statut: Conversation['statut']) => {
    setConversations((cs) => cs?.map((c) => (c._id === id ? { ...c, statut } : c)) ?? cs);
    appeler(`${API_CONFIG.endpoints.textos}/${id}/statut`, 'POST', { statut });
  };

  const basculerAuto = () => {
    setAnalyseAuto(!analyseAuto);
    appeler(API_CONFIG.endpoints.textosReglages, 'PUT', { analyseAuto: !analyseAuto });
  };

  const aTraiter = conversations?.filter((c) => c.statut === 'a_traiter') ?? [];
  const traites = conversations?.filter((c) => c.statut === 'traite') ?? [];
  const aAnalyser = aTraiter.filter((c) => c.analyse?.statut !== 'en_cours' && (c.analyse?.statut !== 'pret' || c.analyse.messagesLus !== c.messages.length));

  return (
    <div className="min-h-screen pt-6 pb-24">
      <div className="mx-auto max-w-2xl space-y-4 px-1 sm:px-6">
        <div className="flex items-end justify-between gap-3">
          <div>
            <h1 className="bg-gradient-to-r from-cyan-300 via-indigo-300 to-purple-300 bg-clip-text text-2xl font-bold text-transparent">
              Textos
            </h1>
            <p className="text-xs text-gray-500">
              {conversations ? `${aTraiter.length} client${aTraiter.length > 1 ? 's' : ''} à qui répondre` : 'Chargement…'}
            </p>
          </div>
          {chargement && <Loader2 className="mb-1 h-4 w-4 animate-spin text-cyan-300" />}
        </div>

        {/* Contrôles : Gemma ne lit les textos que sur demande, sauf en pleine saison */}
        <div className="space-y-3 rounded-xl border border-indigo-500/30 bg-gray-900/50 p-4">
          <button
            onClick={() => analyser()}
            disabled={!aAnalyser.length}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-cyan-400/40 bg-gradient-to-r from-cyan-500/20 to-indigo-500/20 px-3 py-2.5 text-sm font-medium text-cyan-100 transition-all hover:from-cyan-500/30 hover:to-indigo-500/30 disabled:opacity-40"
          >
            {enCours ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {enCours
              ? 'Gemma analyse les textos…'
              : aAnalyser.length
                ? `Analyser ${aAnalyser.length} conversation${aAnalyser.length > 1 ? 's' : ''}`
                : 'Tout est analysé'}
          </button>
          <label className="flex cursor-pointer items-center justify-between gap-3">
            <span>
              <span className="block text-sm text-gray-200">Analyse automatique</span>
              <span className="block text-[11px] text-gray-500">Gemma lit chaque texto de client dès qu'il arrive. Pour la pleine saison.</span>
            </span>
            <button
              role="switch"
              aria-checked={analyseAuto}
              onClick={basculerAuto}
              className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${analyseAuto ? 'bg-cyan-500' : 'bg-gray-600'}`}
            >
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${analyseAuto ? 'left-[22px]' : 'left-0.5'}`} />
            </button>
          </label>
        </div>

        {erreur && (
          <div className="flex items-start gap-2 rounded-xl border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{erreur}</span>
          </div>
        )}

        {conversations && aTraiter.length === 0 && !erreur && (
          <div className="flex flex-col items-center gap-1 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-8 text-center">
            <CheckCircle className="h-7 w-7 text-emerald-300" />
            <p className="text-sm text-gray-200">Aucun texto de client en attente.</p>
          </div>
        )}

        {aTraiter.map((c) => (
          <CarteConversation key={c._id} c={c} onStatut={changerStatut} onAnalyser={(id) => analyser([id])} />
        ))}

        {traites.length > 0 && (
          <>
            <p className="pt-2 text-xs uppercase tracking-wide text-gray-500">Traités cette semaine</p>
            {traites.map((c) => (
              <CarteConversation key={c._id} c={c} onStatut={changerStatut} onAnalyser={(id) => analyser([id])} />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default Textos;
